import type React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GripVertical, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IClaimFormCustomField, TClaimFormFieldType } from '@/@types/claims';
import type { EditorAction } from './editorReducer';

const FIELD_TYPES: TClaimFormFieldType[] = [
  'text', 'number', 'date', 'dropdown', 'file', 'textarea',
  'phone', 'email', 'address', 'currency', 'checkbox', 'signature',
];

// LOCAL_TYPE_JUSTIFIED: editor-internal props — tightly scoped to this feature, not reused elsewhere
// LOCAL_TYPE_JUSTIFIED: editor-internal section option shape — not reused elsewhere
interface ISectionOption {
  key: string;
  label: string;
}

interface ICustomFieldFormProps {
  field: IClaimFormCustomField;
  sections: ISectionOption[];
  dispatch: React.Dispatch<EditorAction>;
}

export function CustomFieldForm({ field, sections, dispatch }: ICustomFieldFormProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={cn('p-4 transition-shadow', isDragging && 'shadow-lg opacity-80 bg-background z-50')}>
        <div className="flex items-start gap-2">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing touch-none p-0.5 mt-1 rounded text-muted-foreground hover:text-foreground shrink-0"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder custom field"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Target Section</Label>
              <Select
                value={field.sectionKey}
                onValueChange={v => dispatch({ type: 'UPDATE_CUSTOM_FIELD', fieldId: field.id, updates: { sectionKey: v } })}
              >
                <SelectTrigger className="h-8 text-sm mt-1">
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  {sections.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Field Type</Label>
              <Select
                value={field.type}
                onValueChange={v => dispatch({ type: 'UPDATE_CUSTOM_FIELD', fieldId: field.id, updates: { type: v as TClaimFormFieldType } })}
              >
                <SelectTrigger className="h-8 text-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(t => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={field.label}
                onChange={e => dispatch({ type: 'UPDATE_CUSTOM_FIELD', fieldId: field.id, updates: { label: e.target.value } })}
                className="h-8 text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Placeholder (optional)</Label>
              <Input
                value={field.placeholder ?? ''}
                onChange={e => dispatch({ type: 'UPDATE_CUSTOM_FIELD', fieldId: field.id, updates: { placeholder: e.target.value || undefined } })}
                className="h-8 text-sm mt-1"
              />
            </div>
            {field.type === 'dropdown' && (
              <div className="sm:col-span-2">
                <Label className="text-xs">Options (comma-separated)</Label>
                <Input
                  value={(field.options ?? []).join(', ')}
                  onChange={e =>
                    dispatch({
                      type: 'UPDATE_CUSTOM_FIELD',
                      fieldId: field.id,
                      updates: { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) },
                    })
                  }
                  className="h-8 text-sm mt-1"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                id={`req-${field.id}`}
                checked={field.required}
                onCheckedChange={v => dispatch({ type: 'UPDATE_CUSTOM_FIELD', fieldId: field.id, updates: { required: v } })}
              />
              <Label htmlFor={`req-${field.id}`} className="text-sm cursor-pointer">Required</Label>
            </div>
            <div className="flex justify-end items-end">
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:text-destructive h-8"
                onClick={() => dispatch({ type: 'REMOVE_CUSTOM_FIELD', fieldId: field.id })}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
